import React, { useMemo, useRef, useState, useEffect } from "react";
import { useDataLayer } from "../../../Contexts/DataLayer.context";

const SelectDropDownContext = ({
    array,
    field,
    fieldValue,
    label,
    value,
    onChange,
}) => {
    const { openSelectDropDown,setOpenSelectDropdown } = useDataLayer();

    // Unique key for THIS dropdown in the global context
    const dropdownKey = label;

    const isOpen = openSelectDropDown === dropdownKey;
    const isActive = value !== "" && value !== null && value !== undefined;

    const baseBorderColor = "#cdcbcbff";
    const activeBorderColor = "#446E9B";
    const borderColor = isActive ? activeBorderColor : baseBorderColor;

    // Label shown in the box
    const selectedLabel = useMemo(() => {
        if (!isActive) return label;
        const match = array?.find((opt) => opt[fieldValue] === value);
        return match ? match[field] : label;
    }, [array, field, fieldValue, label, value, isActive]);

    // --- Typeahead + keyboard navigation (ModDropDown-style) ---
    const dropdownRef = useRef(null);
    const optionRefs = useRef([]);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [typedChars, setTypedChars] = useState("");
    const typingTimeoutRef = useRef(null);

    const totalOptions = array?.length ?? 0;

    const resetTypeahead = () => {
        setTypedChars("");
        // keep highlight if you like, or reset:
        // setHighlightedIndex(-1);
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
        }
    };

    const toggleDropdown = (e) => {
        e.stopPropagation();
        const willOpen = !isOpen;
        setOpenSelectDropdown(willOpen ? dropdownKey : null);

        if (willOpen && array && array.length > 0) {
            // start highlight on the current selection (if any)
            const selectedIndex = array.findIndex(
                (opt) => opt[fieldValue] === value
            );
            setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : -1);
        } else {
            resetTypeahead();
        }
    };

    const handleSelect = (newVal) => {
        const eventLike = { target: { value: newVal } };
        onChange(eventLike);
        setOpenSelectDropdown(null);
        resetTypeahead();
    };

    const handleClear = (e) => {
        e.stopPropagation();
        const eventLike = { target: { value: "" } };
        onChange(eventLike);
        setOpenSelectDropdown(null);
        resetTypeahead();
    };

    const handleTypeAhead = (char) => {
        if (!array || !array.length) return;

        const newTyped = (typedChars + char.toLowerCase()).trim();
        setTypedChars(newTyped);

        const idx = array.findIndex((opt) =>
            String(opt[field] ?? "").toLowerCase().startsWith(newTyped)
        );
        if (idx >= 0) {
            setHighlightedIndex(idx);
        }

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }
        typingTimeoutRef.current = window.setTimeout(() => {
            setTypedChars("");
            // leave highlightedIndex as-is so suggestion doesn’t “fade”
        }, 1000);
    };

    const handleKeyDown = (event) => {
        if (!isOpen) return;

        const { key } = event;

        switch (key) {
            case "ArrowDown":
                event.preventDefault();
                if (!totalOptions) return;
                setHighlightedIndex((prev) => {
                    if (prev < 0) return 0;
                    return Math.min(prev + 1, totalOptions - 1);
                });
                break;

            case "ArrowUp":
                event.preventDefault();
                if (!totalOptions) return;
                setHighlightedIndex((prev) => {
                    if (prev < 0) return totalOptions - 1;
                    return Math.max(prev - 1, 0);
                });
                break;

            case "Enter":
                event.preventDefault();
                if (
                    highlightedIndex >= 0 &&
                    highlightedIndex < totalOptions &&
                    array
                ) {
                    const selectedOpt = array[highlightedIndex];
                    handleSelect(selectedOpt[fieldValue]);
                }
                break;

            case "Escape":
                event.preventDefault();
                setOpenSelectDropdown(null);
                resetTypeahead();
                break;

            default:
                if (/^[a-zA-Z0-9\s]$/.test(key)) {
                    event.preventDefault();
                    handleTypeAhead(key);
                }
                break;
        }
    };

    // Global keydown listener while the dropdown is open
    useEffect(() => {
        if (!isOpen) return;

        const listener = (e) => handleKeyDown(e);
        document.addEventListener("keydown", listener);
        return () => {
            document.removeEventListener("keydown", listener);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, highlightedIndex, typedChars, array, value]);

    // Auto-scroll to keep highlighted item centered
    useEffect(() => {
        if (!isOpen) return;
        if (highlightedIndex < 0) return;
        if (!dropdownRef.current) return;

        const dropdownEl = dropdownRef.current;
        const itemEl = optionRefs.current[highlightedIndex];
        if (!dropdownEl || !itemEl) return;

        const dropdownHeight = dropdownEl.clientHeight;
        const itemTop = itemEl.offsetTop;
        const itemHeight = itemEl.offsetHeight;
        const scrollTo = itemTop - dropdownHeight / 2 + itemHeight / 2;

        dropdownEl.scrollTop = scrollTo;
    }, [highlightedIndex, isOpen]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="position-relative w-100">
            {/* Wrapper with single border + radius (like FBXCalendar) */}
            <div
                className="d-flex w-100"
                onClick={toggleDropdown}
                style={{
                    border: `2px solid ${borderColor}`,
                    borderRadius: "4px",
                    overflow: "hidden", // clips inner corners
                    cursor: "pointer",
                }}
            >
                {/* Label / value area */}
                <div
                    className="flex-grow-1 px-2 d-flex align-items-center border-0 shadow-none bg-white"
                    style={{
                        minHeight: "33px",
                        fontSize: "0.8rem",
                        fontWeight: 400,
                        borderRadius: 0, // wrapper owns radius
                    }}
                    title={selectedLabel}
                >
                    <span className="text-truncate">{selectedLabel}</span>
                </div>

                {/* X button inside the same wrapper (no seam) */}
                <button
                    type="button"
                    className={`btn btn-sm border-0 shadow-none bg-white ${isActive ? "text-muted" : "text-white"}`}
                    style={{
                        minWidth: "32px",
                        borderRadius: 0, // wrapper owns radius
                    }}
                    disabled={!isActive}
                    onClick={handleClear}
                >
                    X
                </button>
            </div>

            {/* Dropdown list */}
            {isOpen && (
                <div
                    ref={dropdownRef}
                    className="bg-white border rounded shadow-sm mt-1 position-absolute w-100"
                    style={{
                        zIndex: 2050,
                        maxHeight: "220px",
                        overflowY: "auto",
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {array && array.length > 0 ? (
                        array.map((opt, idx) => {
                            const optValue = opt[fieldValue];
                            const optLabel = opt[field];
                            const highlighted = idx === highlightedIndex;

                            let btnClass = "btn btn-sm w-100 text-start ";
                            if (highlighted) {
                                btnClass += "btn-primary text-white";
                            } else {
                                btnClass += "btn-light text-dark";
                            }

                            return (
                                <button
                                    key={optValue ?? idx}
                                    type="button"
                                    ref={(el) => (optionRefs.current[idx] = el)}
                                    className={btnClass}
                                    style={{ fontSize: "0.8rem" }}
                                    onClick={() => handleSelect(optValue)}
                                >
                                    {optLabel}
                                </button>
                            );
                        })
                    ) : (
                        <div className="px-2 py-1 small text-muted">
                            <em>No options</em>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SelectDropDownContext;
